import {
  branch,
  cash_movement,
  combo,
  combo_item,
  customer,
  dining_table,
  employee,
  ingredient,
  isNull,
  loyalty_movement,
  menu_category,
  menu_product,
  modifier_group,
  modifier_option,
  order,
  order_line,
  order_line_modifier,
  payment,
  product_modifier_group,
  product_variant,
  promotion,
  purchase_order,
  purchase_order_line,
  recipe_item,
  register_session,
  reservation,
  sql,
  stock_movement,
  supplier,
} from "@comandero/powersync";
import type { AnySQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CAPA LIVE CENTRALIZADA (choke point único)                  │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Este módulo es el ÚNICO lugar donde el invariante «fila viva = `deleted_at IS
 * NULL`» se expresa para las 27 tablas con papelera/anulación de este repo POS.
 * Toda lectura viva compone estos predicados (los builders de dominio en
 * menu/orders/inventory/… y la capa live los importan de aquí). Las ÚNICAS
 * lecturas que ven filas borradas son la papelera unificada
 * (`trash/queries.ts`, con `.from(<var>)` dinámico que el guard no toca) y algún
 * lookup id-scoped inofensivo, declarados en la whitelist del guard
 * (`guard-manifest.ts`).
 *
 * ¿Por qué centralizar? El invariante disperso fue la familia de bugs #1 de la
 * campaña (búsqueda ⌘K y contador checkUnsyncedActor que ignoraban `deleted_at`;
 * reportes que sumaban líneas/pagos de órdenes anuladas). Con un choke point
 * único + guard anti-regresión, añadir una lectura cruda de estas tablas fuera de
 * la whitelist ROMPE el build (ver `live/__tests__/live-access-guard.test.ts`).
 *
 * ─── Jerarquía de cohortes (POS) ────────────────────────────────────────────
 * Una fila HIJA está viva sólo si su propio `deleted_at IS NULL` Y su padre con
 * papelera no está en la papelera (cohorte `NOT EXISTS`). Verificado contra los
 * FKs lógicos de `packages/powersync/src/schema.ts` (no hay FKs físicos en la BD:
 * PowerSync los modela lógicamente):
 *   - menu_product        → menu_category (category_id)
 *   - product_variant     → menu_product  (product_id)
 *   - product_modifier_group → menu_product (product_id) + modifier_group (pivote)
 *   - modifier_option     → modifier_group (modifier_group_id)
 *   - combo_item          → combo          (combo_id)
 *   - recipe_item         → menu_product   (product_id)  [dueño; ingredient es ref]
 *   - order_line          → order          (order_id)
 *   - order_line_modifier → order_line     (order_line_id)  [directo; ver nota]
 *   - payment             → order          (order_id)
 *   - purchase_order_line → purchase_order (purchase_order_id)
 *   - loyalty_movement    → customer       (customer_id)
 *   - stock_movement      → ingredient     (ingredient_id)
 * Las demás tablas soft-delete son raíces (catálogo top-level o transaccionales
 * operativas: branch, customer, employee, ingredient, menu_category, combo,
 * modifier_group, promotion, supplier, order, register_session, cash_movement,
 * dining_table, reservation, purchase_order) → sólo su propio `deleted_at`.
 *
 * NOTA (cohorte de un nivel): la cohorte comprueba el PADRE DIRECTO (patrón del
 * piloto/tendly). p.ej. `order_line_modifier` mira su `order_line`, no el `order`
 * abuelo — pero en las vistas vivas los modificadores sólo se leen dentro de su
 * línea/orden ya viva, así que el borde no aparece.
 */

/**
 * Predicado de cohorte genérico: la fila hija cuelga de un padre que NO está en
 * la papelera. Con soft-delete del padre los hijos persisten intactos (para
 * restaurar la cohorte entera) pero desaparecen de las vistas vivas.
 */
function parentNotTrashed(
  parent: SQLiteTable & { id: AnySQLiteColumn; deleted_at: AnySQLiteColumn },
  childFk: AnySQLiteColumn,
) {
  return sql`NOT EXISTS (SELECT 1 FROM ${parent} WHERE ${parent.id} = ${childFk} AND ${parent.deleted_at} IS NOT NULL)`;
}

// ─── Raíces (sólo su propio deleted_at) ──────────────────────────────────────

export function liveBranchFilters() {
  return [isNull(branch.deleted_at)];
}

export function liveMenuCategoryFilters() {
  return [isNull(menu_category.deleted_at)];
}

export function liveModifierGroupFilters() {
  return [isNull(modifier_group.deleted_at)];
}

export function liveComboFilters() {
  return [isNull(combo.deleted_at)];
}

export function liveIngredientFilters() {
  return [isNull(ingredient.deleted_at)];
}

export function liveCustomerFilters() {
  return [isNull(customer.deleted_at)];
}

export function livePromotionFilters() {
  return [isNull(promotion.deleted_at)];
}

export function liveEmployeeFilters() {
  return [isNull(employee.deleted_at)];
}

export function liveSupplierFilters() {
  return [isNull(supplier.deleted_at)];
}

export function livePurchaseOrderFilters() {
  return [isNull(purchase_order.deleted_at)];
}

export function liveDiningTableFilters() {
  return [isNull(dining_table.deleted_at)];
}

export function liveReservationFilters() {
  return [isNull(reservation.deleted_at)];
}

export function liveRegisterSessionFilters() {
  return [isNull(register_session.deleted_at)];
}

export function liveCashMovementFilters() {
  return [isNull(cash_movement.deleted_at)];
}

export function liveOrderFilters() {
  return [isNull(order.deleted_at)];
}

// ─── Hijos con cohorte (padre con papelera no borrado) ───────────────────────

export function liveMenuProductFilters() {
  return [
    isNull(menu_product.deleted_at),
    parentNotTrashed(menu_category, menu_product.category_id),
  ];
}

export function liveProductVariantFilters() {
  return [
    isNull(product_variant.deleted_at),
    parentNotTrashed(menu_product, product_variant.product_id),
  ];
}

export function liveProductModifierGroupFilters() {
  return [
    isNull(product_modifier_group.deleted_at),
    parentNotTrashed(menu_product, product_modifier_group.product_id),
    parentNotTrashed(modifier_group, product_modifier_group.modifier_group_id),
  ];
}

export function liveModifierOptionFilters() {
  return [
    isNull(modifier_option.deleted_at),
    parentNotTrashed(modifier_group, modifier_option.modifier_group_id),
  ];
}

export function liveComboItemFilters() {
  return [isNull(combo_item.deleted_at), parentNotTrashed(combo, combo_item.combo_id)];
}

export function liveRecipeItemFilters() {
  return [isNull(recipe_item.deleted_at), parentNotTrashed(menu_product, recipe_item.product_id)];
}

export function liveOrderLineFilters() {
  return [isNull(order_line.deleted_at), parentNotTrashed(order, order_line.order_id)];
}

export function liveOrderLineModifierFilters() {
  return [
    isNull(order_line_modifier.deleted_at),
    parentNotTrashed(order_line, order_line_modifier.order_line_id),
  ];
}

export function livePaymentFilters() {
  return [isNull(payment.deleted_at), parentNotTrashed(order, payment.order_id)];
}

export function livePurchaseOrderLineFilters() {
  return [
    isNull(purchase_order_line.deleted_at),
    parentNotTrashed(purchase_order, purchase_order_line.purchase_order_id),
  ];
}

export function liveLoyaltyMovementFilters() {
  return [
    isNull(loyalty_movement.deleted_at),
    parentNotTrashed(customer, loyalty_movement.customer_id),
  ];
}

export function liveStockMovementFilters() {
  return [
    isNull(stock_movement.deleted_at),
    parentNotTrashed(ingredient, stock_movement.ingredient_id),
  ];
}
